/**
 * The Chord Legend: every chord formula MusicMentor supports, in one place.
 *
 * There is deliberately no chord data in this file. The name, the symbol, the
 * formula, the example notes, the notation and the sound all come from the same
 * `ChordQuality` record the builder, the recogniser, the engraver and the
 * speaker already use — so a screen whose entire job is to *state the formula*
 * cannot state a different one from the app that builds it.
 *
 * The formula itself is computed by `chordFormula`, from the quality's degree
 * structure. A hand-written `'1–3–5–♭7'` here would agree with `spellChord` on
 * the day it was typed and drift the first time a quality changed, and the drift
 * would be a reference page teaching a chord the rest of the app does not build.
 */

import { useMemo, useState } from 'react';
import {
  CHORD_CATEGORIES,
  QUALITIES,
  chordFormula,
  commonVoicingDegrees,
  spellChord,
  type ChordCategory,
  type ChordQuality,
} from '../theory/chords';
import { PITCH_CLASSES, pitchClassName, type AccidentalPreference } from '../theory/voicing';
import { pitchName, type Pitch } from '../theory/pitch';
import { playPitches, stop } from '../audio/player';
import { Score } from '../components/Score';
import { Button } from '../design/Button';
import { SegmentedControl } from '../design/Navigation';
import type { BuildRequest, Destination } from '../auth/routes';

export interface ChordLegendProps {
  go: (destination: Destination, build?: BuildRequest) => void;
}

/**
 * The root a legend entry is shown at.
 *
 * A pitch class plus a spelling preference, exactly as the Chord FIB picker
 * models it — which is what lets "Build in FIB" hand over a root that arrives
 * meaning the same thing. The octave is the app's business, not the learner's.
 */
function rootPitch(pitchClassValue: number, preference: AccidentalPreference): Pitch {
  const name = pitchClassName(pitchClassValue, preference);
  const letter = name[0] as Pitch['letter'];
  const alter = name.includes('♯') ? 1 : name.includes('♭') ? -1 : 0;
  return { letter, alter, octave: 4 };
}

/**
 * Ordinary words for the degrees, so "major 7" and "major seventh" both land on
 * `maj7`. Applied to the query *and* to the chord's name, so the two meet in the
 * middle rather than the query having to guess the app's vocabulary.
 *
 * Deliberately only the number words. Mapping "minor" to "m" would make a search
 * for "minor" match "Major seventh", because that name begins with one.
 */
const NUMBER_WORDS: [RegExp, string][] = [
  [/thirteenth/g, '13'],
  [/eleventh/g, '11'],
  [/ninth/g, '9'],
  [/seventh/g, '7'],
  [/sixth/g, '6'],
  [/fifth/g, '5'],
  [/fourth/g, '4'],
  [/third/g, '3'],
  [/second/g, '2'],
];

/**
 * One spelling for accidentals, dashes, spaces and case.
 *
 * Nothing else. This is what an *exact* comparison uses, so it may only
 * normalise things that are the same character written differently — ♭ and b
 * are one accidental; "seventh" and "7" are two different strings a learner
 * might type, and collapsing them here turns a browse into an exact hit. That
 * is what made `ninth` return only the dominant ninth, because the symbol of
 * that one chord is `9`.
 */
function normaliseStrict(text: string): string {
  return text
    .toLowerCase()
    .replace(/♭/g, 'b')
    .replace(/♯/g, '#')
    .replace(/𝄫/g, 'bb')
    .replace(/[\s()–—-]/g, '');
}

/**
 * The same, plus ordinary words for the degrees.
 *
 * Only for the substring pass, where "major 7" should reach `maj7` without the
 * learner having to guess the app's vocabulary.
 */
function normaliseLoose(text: string): string {
  let out = normaliseStrict(text);
  for (const [pattern, replacement] of NUMBER_WORDS) out = out.replace(pattern, replacement);
  return out;
}

/**
 * The strings that *name* this chord exactly.
 *
 * The internal `id` is deliberately absent. It is an identifier, not a name, and
 * treating it as one is what made `m7b5` return two chords: the half-diminished
 * seventh because that is its id, and the dominant 7♭5 because "m7b5" happens to
 * contain "7b5". Where a printed symbol is a glyph nobody can type, the domain
 * says what a learner would type instead — see `ChordQuality.aliases`.
 */
function exactNames(quality: ChordQuality): string[] {
  return [quality.name, quality.symbol, ...(quality.aliases ?? []), chordFormula(quality)]
    .filter((text) => text !== '')
    .map(normaliseStrict);
}

/** Everything worth searching loosely, including the id. */
function looseHaystacks(quality: ChordQuality): string[] {
  return [
    quality.name,
    quality.symbol,
    quality.id,
    ...(quality.aliases ?? []),
    chordFormula(quality),
  ].map(normaliseLoose);
}

export function exactlyMatches(quality: ChordQuality, query: string): boolean {
  const needle = normaliseStrict(query);
  return needle !== '' && exactNames(quality).includes(needle);
}

/** Does this quality match what was typed, anywhere in a name, symbol or formula? */
export function matchesSearch(quality: ChordQuality, query: string): boolean {
  const needle = normaliseLoose(query);
  if (!needle) return true;
  return looseHaystacks(quality).some((text) => text.includes(needle));
}

/**
 * Search a set of qualities, exact names first.
 *
 * Typing a chord's name should give you that chord. Substring matching alone
 * cannot do that — every name is a substring of some longer name — so an exact
 * hit wins outright and the loose search is what happens when there is no exact
 * one. That keeps `m7` meaning the minor seventh while `sus`, `dim` and `add`
 * stay the browsing searches they read as.
 */
export function searchQualities(pool: ChordQuality[], query: string): ChordQuality[] {
  if (!normaliseStrict(query)) return pool;
  const exact = pool.filter((quality) => exactlyMatches(quality, query));
  if (exact.length) return exact;
  return pool.filter((quality) => matchesSearch(quality, query));
}

export function ChordLegend({ go }: ChordLegendProps) {
  const [query, setQuery] = useState('');
  const [category, setCategory] = useState<ChordCategory | 'all'>('all');
  const [rootClass, setRootClass] = useState(0);
  const [preference, setPreference] = useState<AccidentalPreference>('flat');

  const root = useMemo(() => rootPitch(rootClass, preference), [rootClass, preference]);

  const shown = useMemo(() => {
    // Category first, then the search within it: an exact name should win
    // against the chords the learner is currently looking at, not against the
    // whole catalogue they have already filtered away.
    const inCategory = QUALITIES.filter(
      (quality) => category === 'all' || quality.categories.includes(category),
    );
    return searchQualities(inCategory, query);
  }, [category, query]);

  return (
    <section className="feature chord-legend">
      <header className="feature-head">
        <div>
          <p className="eyebrow">Chord Legend</p>
          <h2>Chord Legend</h2>
          <p className="lede">
            A chord formula tells you which notes to build from the root. The numbers count
            up the scale from 1. A ♭ lowers a note by a half step; a ♯ raises it.
          </p>
        </div>
      </header>

      <div className="legend-controls">
        <label className="field">
          <span>Search</span>
          <input
            type="search"
            value={query}
            placeholder="maj7, m7♭5, sus, dim…"
            onChange={(event) => setQuery(event.target.value)}
            aria-label="Search chords by name or symbol"
          />
        </label>

        <label className="field">
          <span>Root</span>
          <select
            value={rootClass}
            onChange={(event) => setRootClass(Number(event.target.value))}
            aria-label="Root note"
          >
            {PITCH_CLASSES.map((pc) => (
              <option key={pc} value={pc}>
                {pitchClassName(pc, preference)}
              </option>
            ))}
          </select>
        </label>

        <SegmentedControl
          label="Spelling"
          value={preference}
          onChange={(next) => setPreference(next as AccidentalPreference)}
          options={[
            { value: 'flat', label: '♭' },
            { value: 'sharp', label: '♯' },
          ]}
        />
      </div>

      <div className="legend-filters" role="group" aria-label="Chord categories">
        {[{ id: 'all' as const, label: 'All' }, ...CHORD_CATEGORIES].map((option) => (
          <button
            key={option.id}
            type="button"
            className="mm-note-chip"
            aria-pressed={category === option.id}
            onClick={() => setCategory(option.id as ChordCategory | 'all')}
          >
            {option.label}
          </button>
        ))}
      </div>

      <p className="legend-count" role="status">
        {shown.length} of {QUALITIES.length} chord{QUALITIES.length === 1 ? '' : 's'}
      </p>

      {shown.length === 0 ? (
        <p className="empty">
          Nothing matches “{query}”. Try a symbol like <code>m7</code> or a word like{' '}
          <code>diminished</code>.
        </p>
      ) : (
        <ul className="legend-list">
          {shown.map((quality) => (
            <LegendEntry
              key={quality.id}
              quality={quality}
              root={root}
              go={go}
              rootClass={rootClass}
              spelling={preference}
            />
          ))}
        </ul>
      )}
    </section>
  );
}

interface EntryProps {
  quality: ChordQuality;
  root: Pitch;
  rootClass: number;
  /**
   * Which way this page is spelling its root — and the half of the hand-over
   * that used to be dropped.
   *
   * A pitch class is not a spelling. Sending only `rootClass: 1` meant the
   * learner looking at C♯maj7 arrived in the builder at D♭maj7, because that is
   * what the builder defaults to. Same sound, different chord on the page, and
   * the Legend exists precisely to teach that the difference matters.
   */
  spelling: AccidentalPreference;
  go: ChordLegendProps['go'];
}

function LegendEntry({ quality, root, rootClass, spelling, go }: EntryProps) {
  // One spelled chord, used for the note names, the staff and the sound. Three
  // surfaces computing it separately is how they come to disagree.
  const pitches = useMemo(() => spellChord(root, quality), [root, quality]);
  const chordName = `${pitchName(root)}${quality.symbol}`;
  const voicing = commonVoicingDegrees(quality);
  const isReduced = voicing.length !== quality.tones.length;

  return (
    <li className="legend-card">
      <div className="legend-card__head">
        <h3 className="legend-card__name">{quality.name}</h3>
        <p className="legend-card__symbol">{quality.symbol || '(no symbol)'}</p>
      </div>

      <p className="legend-card__formula" aria-label={`Formula: ${chordFormula(quality)}`}>
        {chordFormula(quality)}
      </p>

      <p className="legend-card__blurb">{quality.blurb}</p>

      <p className="legend-card__example">
        For {pitchName(root)}: <strong>{chordName}</strong> is{' '}
        <span className="legend-card__notes">{pitches.map(pitchName).join('–')}</span>
      </p>

      {isReduced ? (
        <p className="legend-card__voicing">
          Players often leave out the {quality.commonlyOmitted!.join(' and ')} and play{' '}
          {voicing.join('–')}. That is a voicing, not the formula.
        </p>
      ) : null}

      <div className="legend-card__score">
        <Score clef="treble" groups={[pitches]} height={150} labels={[chordName]} />
      </div>

      {/*
        Twenty-seven cards, three buttons each. The visible labels stay short
        because they sit under the chord they belong to — but a screen reader
        rattling off "Hear chord, Stop, Build in FIB" eighty-one times says
        nothing about *which* chord, so each one carries its own name.
      */}
      <div className="legend-card__actions">
        <Button
          variant="primary"
          icon="play"
          aria-label={`Hear ${chordName}`}
          onClick={() => void playPitches(pitches, 'both')}
        >
          Hear chord
        </Button>
        <Button variant="tertiary" icon="stop" aria-label={`Stop ${chordName}`} onClick={stop}>
          Stop
        </Button>
        <Button
          variant="secondary"
          aria-label={`Build ${chordName} in FIB`}
          onClick={() => go('fib', { rootClass, qualityId: quality.id, spelling })}
        >
          Build in FIB
        </Button>
      </div>
    </li>
  );
}
